// =============================================================================
// Origin UI — src/pages/tasks/TasksQueuePage.tsx
// IT Ops task queue — grouped by request (accordion), sorted by urgency.
// Phase 3, Tasks 3.7 + 3.8: Sort by urgency, group by request, effective date.
// =============================================================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskApi } from '@/services/requestApi';
import { api } from '@/services/apiService';
import { StatusBadge, LoadingSpinner, EmptyState, PageHeader, Card, ErrorBanner } from '@/components/common';
import type { TaskListItem } from '@/types/api';
import styles from './tasks.module.css';

interface ITStaffMember {
  id: string;
  fullName: string;
  email: string;
  entraObjectId: string;
}

// ─── Urgency helpers ───

function daysUntil(dateStr: string): number {
  const target = new Date(dateStr);
  const now = new Date();
  // Zero out time for day-level comparison
  target.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

function getUrgency(dateStr: string): 'overdue' | 'urgent' | 'soon' | 'normal' {
  const days = daysUntil(dateStr);
  if (days < 0) return 'overdue';
  if (days <= 2) return 'urgent';
  if (days <= 7) return 'soon';
  return 'normal';
}

function urgencyLabel(dateStr: string): string {
  const days = daysUntil(dateStr);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return `${days}d away`;
}

// ─── Group tasks by requestId ───

interface RequestGroup {
  requestId: string;
  fullName: string;
  employeeId: string;
  requestType: string;
  effectiveDate: string;
  department: string;
  tasks: TaskListItem[];
  urgency: ReturnType<typeof getUrgency>;
  openCount: number;
  totalCount: number;
}

function groupByRequest(tasks: TaskListItem[]): RequestGroup[] {
  const map = new Map<string, RequestGroup>();

  tasks.forEach(t => {
    if (!map.has(t.requestId)) {
      map.set(t.requestId, {
        requestId: t.requestId,
        fullName: t.requestFullName,
        employeeId: t.requestOqtEmployeeId,
        requestType: t.requestType,
        effectiveDate: t.effectiveDate || '',
        department: t.department || '',
        tasks: [],
        urgency: 'normal',
        openCount: 0,
        totalCount: 0,
      });
    }
    const group = map.get(t.requestId)!;
    group.tasks.push(t);
    group.totalCount++;
    if (t.status !== 'Completed') group.openCount++;
  });

  // Calculate urgency per group
  map.forEach(group => {
    if (group.effectiveDate) {
      group.urgency = getUrgency(group.effectiveDate);
    }
  });

  // Sort: completed groups last, then overdue first, urgent, soon, normal.
  // Within same urgency, nearest date first.
  const urgencyOrder = { overdue: 0, urgent: 1, soon: 2, normal: 3 };
  return Array.from(map.values()).sort((a, b) => {
    // Completed groups always sort after active groups
    const aCompleted = a.openCount === 0;
    const bCompleted = b.openCount === 0;
    if (aCompleted !== bCompleted) return aCompleted ? 1 : -1;

    const ua = urgencyOrder[a.urgency];
    const ub = urgencyOrder[b.urgency];
    if (ua !== ub) return ua - ub;
    // Same urgency — sort by effective date ascending
    return (a.effectiveDate || '').localeCompare(b.effectiveDate || '');
  });
}

export function TasksQueuePage() {
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(new Set());
  const [itStaff, setITStaff] = useState<ITStaffMember[]>([]);
  const [assigningRequest, setAssigningRequest] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadTasks();
    loadITStaff();
  }, []);

  async function loadITStaff() {
    try {
      const staff = await api.get<ITStaffMember[]>('/api/reference/it-staff');
      setITStaff(staff);
    } catch { /* IT Staff dropdown is optional, fail silently */ }
  }

  async function loadTasks() {
    try {
      setLoading(true);
      const data = await taskApi.list();
      setTasks(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }

  function toggleExpand(requestId: string) {
    setExpandedRequests(prev => {
      const next = new Set(prev);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }
      return next;
    });
  }

  function expandAll() {
    setExpandedRequests(new Set(groups.map(g => g.requestId)));
  }

  function collapseAll() {
    setExpandedRequests(new Set());
  }

  async function handleBulkAssign(requestId: string, staffMember: ITStaffMember) {
    try {
      setError('');
      await api.post(`/api/requests/${requestId}/assign-all`, {
        assignedTo: staffMember.entraObjectId,
        assignedToDisplayName: staffMember.fullName,
      });
      setAssigningRequest(null);
      await loadTasks();
    } catch (err: any) {
      setError(err.message || 'Failed to assign tasks');
    }
  }

  // Derive unique assignees for filter dropdown
  const assignees = Array.from(
    new Set(tasks.filter(t => t.assignedToDisplayName).map(t => t.assignedToDisplayName!))
  ).sort();

  // Apply filters
  const filtered = tasks.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (assigneeFilter === 'unassigned' && t.assignedTo) return false;
    if (assigneeFilter !== 'all' && assigneeFilter !== 'unassigned' && t.assignedToDisplayName !== assigneeFilter) return false;
    return true;
  });

  // Group filtered tasks
  const groups = groupByRequest(filtered);

  // Status counts (from all tasks, not filtered)
  const statusCounts: Record<string, number> = {};
  tasks.forEach(t => { statusCounts[t.status] = (statusCounts[t.status] || 0) + 1; });
  const unassignedCount = tasks.filter(t => !t.assignedTo).length;
  const allGroups = groupByRequest(tasks);
  const completedEmployeeCount = allGroups.filter(g => g.openCount === 0).length;

  if (loading) return <LoadingSpinner message="Loading tasks..." />;

  return (
    <div>
      <PageHeader
        title="Tasks Queue"
        subtitle={`${groups.length} employee${groups.length !== 1 ? 's' : ''} · ${tasks.length} task${tasks.length !== 1 ? 's' : ''}`}
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      {/* Filters */}
      <div className={styles.filterRow}>
        <div className={styles.filterBar}>
          <button
            className={`${styles.filterPill} ${statusFilter === 'all' ? styles.filterActive : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All ({tasks.length})
          </button>
          {['Pending', 'InProgress', 'Blocked', 'Completed'].map(s => {
            const count = s === 'Completed' ? completedEmployeeCount : (statusCounts[s] || 0);
            if (count === 0) return null;
            const label = s === 'InProgress' ? 'In Progress' : s;
            const unit = s === 'Completed' ? (count === 1 ? 'employee' : 'employees') : '';
            return (
              <button
                key={s}
                className={`${styles.filterPill} ${statusFilter === s ? styles.filterActive : ''}`}
                onClick={() => setStatusFilter(s)}
              >
                {label} ({count}{unit ? ` ${unit}` : ''})
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            className={styles.filterSelect}
            value={assigneeFilter}
            onChange={e => setAssigneeFilter(e.target.value)}
          >
            <option value="all">All assignees</option>
            <option value="unassigned">Unassigned ({unassignedCount})</option>
            {assignees.map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <button className={styles.btnGhost} onClick={expandAll} type="button" style={{ fontSize: 12, padding: '4px 8px' }}>Expand All</button>
          <button className={styles.btnGhost} onClick={collapseAll} type="button" style={{ fontSize: 12, padding: '4px 8px' }}>Collapse All</button>
        </div>
      </div>

      {groups.length === 0 ? (
        <Card>
          <EmptyState
            icon="📦"
            title="No tasks found"
            message={statusFilter !== 'all' || assigneeFilter !== 'all'
              ? 'Try adjusting your filters.'
              : 'Tasks will appear here when requests are approved.'}
          />
        </Card>
      ) : (
        <div className={styles.accordionList}>
          {groups.map(group => {
            const isExpanded = expandedRequests.has(group.requestId);
            const completedCount = group.totalCount - group.openCount;
            const allCompleted = group.openCount === 0;
            const typeIcon = group.requestType === 'Onboarding' ? '🟢' : group.requestType === 'Offboarding' ? '🔴' : '🔄';

            return (
              <Card key={group.requestId} padding={false} className={styles.accordionCard}>
                {/* Accordion Header */}
                <button
                  className={`${styles.accordionHeader} ${allCompleted ? '' : (styles[`urgency_${group.urgency}`] || '')}`}
                  onClick={() => toggleExpand(group.requestId)}
                  type="button"
                >
                  <span className={styles.accordionExpander}>{isExpanded ? '▾' : '▸'}</span>

                  <div className={styles.accordionInfo}>
                    <span className={styles.accordionName}>{group.fullName}</span>
                    <span className={styles.accordionMeta}>
                      {typeIcon} {group.requestType}
                      {group.department && ` · ${group.department}`}
                      {group.employeeId && ` · ${group.employeeId}`}
                    </span>
                  </div>

                  <div className={styles.accordionRight}>
                    {allCompleted ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 700,
                        background: '#E6F7F0', color: '#007A4E',
                      }}>
                        ✓ Completed
                      </span>
                    ) : (
                      <>
                        {group.effectiveDate && (
                          <span className={`${styles.urgencyBadge} ${styles[`urgencyBadge_${group.urgency}`] || ''}`}>
                            {urgencyLabel(group.effectiveDate)}
                          </span>
                        )}
                      </>
                    )}
                    {group.effectiveDate && (
                      <span className={styles.accordionDate}>
                        {new Date(group.effectiveDate).toLocaleDateString()}
                      </span>
                    )}
                    <span className={styles.accordionTaskCount}>
                      {completedCount}/{group.totalCount} done
                    </span>
                  </div>
                </button>

                {/* Assign All dropdown — shows when there are unassigned tasks */}
                {group.tasks.some(t => !t.assignedTo && t.status !== 'Completed') && (
                  <div style={{ padding: '0 16px 8px', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-end' }}>
                    {assigningRequest === group.requestId ? (
                      <>
                        <select
                          className={styles.filterSelect}
                          style={{ minWidth: '200px' }}
                          defaultValue=""
                          onChange={e => {
                            const staff = itStaff.find(s => s.entraObjectId === e.target.value);
                            if (staff) handleBulkAssign(group.requestId, staff);
                          }}
                        >
                          <option value="" disabled>Select IT staff member...</option>
                          {itStaff.map(s => (
                            <option key={s.id} value={s.entraObjectId}>{s.fullName}</option>
                          ))}
                        </select>
                        <button
                          className={styles.btnGhost}
                          onClick={() => setAssigningRequest(null)}
                          style={{ fontSize: 12, padding: '4px 8px' }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className={styles.btnGhost}
                        onClick={(e) => { e.stopPropagation(); setAssigningRequest(group.requestId); }}
                        style={{ fontSize: 12, padding: '4px 10px', color: '#FF8300', border: '1px solid #FF8300', borderRadius: '6px' }}
                      >
                        👤 Assign ticket
                      </button>
                    )}
                  </div>
                )}

                {/* Expanded Task List */}
                {isExpanded && (
                  <div className={styles.accordionBody}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Task</th>
                          <th>Status</th>
                          <th>Assigned To</th>
                          <th>Created</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.tasks.map(task => (
                          <tr
                            key={task.taskId}
                            className={styles.tableRow}
                            onClick={() => navigate(`/tasks/${task.taskId}`)}
                          >
                            <td>
                              <div className={styles.taskCell}>
                                <span className={styles.taskType}>{task.taskType}</span>
                                {task.description && (
                                  <span className={styles.taskDesc}>
                                    {task.description.length > 60
                                      ? task.description.slice(0, 60) + '…'
                                      : task.description}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td><StatusBadge status={task.status} /></td>
                            <td>
                              {task.assignedToDisplayName
                                ? <span className={styles.assignee}>{task.assignedToDisplayName}</span>
                                : <span className={styles.unassigned}>Unassigned</span>
                              }
                            </td>
                            <td className={styles.muted}>
                              {new Date(task.createdAt).toLocaleDateString()}
                            </td>
                            <td>
                              <span className={styles.viewLink}>View →</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
